'use client';

import { Alert, Button as AntButton, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { AssetLogListItem } from '@evimo/types/api/goldenRecord/find-asset-logs/find-asset-logs.response.dto';
import type { UseAssetLogsResult } from '~/app/hooks/assets/useAssetLogs';
import type { UpdateAssetLogRequest } from '@evimo/types/api/goldenRecord/update-asset-log/update-asset-log.request.dto';
import { Card } from 'antd';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { Loader } from '~/app/components/common/Loader/Loader';
import { AssetLogExpandedDetails } from '~/app/components/asset-detail/AssetLogExpandedDetails';
import { AssetLogModal } from '~/app/components/asset-detail/AssetLogModal';
import { AssetLogsTableToolbar } from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTableToolbar';
import { useAssetLogsTableColumns } from '~/app/components/asset-detail/AssetLogsTable/hooks/useAssetLogsTableColumns';
import { useAssetLogsTableState } from '~/app/components/asset-detail/AssetLogsTable/hooks/useAssetLogsTableState';
import styles from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTable.module.css';

type Props = {
  assetLogs: UseAssetLogsResult;
  isUpdatingLog: boolean;
  onUpdateLog: (logId: string, payload: UpdateAssetLogRequest) => Promise<void>;
};

export function AssetLogsTable({
  assetLogs,
  isUpdatingLog,
  onUpdateLog,
}: Props) {
  const { t } = useTranslation('assets');
  const { columns } = useAssetLogsTableColumns();
  const state = useAssetLogsTableState({ assetLogs });

  const dateRangeValue: [Dayjs, Dayjs] | null =
    assetLogs.filters.from && assetLogs.filters.to
      ? [dayjs(assetLogs.filters.from), dayjs(assetLogs.filters.to)]
      : null;

  if (assetLogs.isError) {
    return <Alert type="error" showIcon title={t('logsError')} />;
  }

  const handleUpdateLog = async (payload: UpdateAssetLogRequest) => {
    if (!state.selectedLog) {
      return;
    }

    await onUpdateLog(state.selectedLog.logId, payload);
    state.setSelectedLog(null);
  };

  return (
    <>
      <Card
        className={`evimo-table-card ${styles.logsTableCard}`}
        styles={{ body: { padding: 0 } }}
        title={
          <AssetLogsTableToolbar
            filters={assetLogs.filters}
            searchValue={state.searchValue}
            onSearchChange={state.setSearchValue}
            onFiltersChange={assetLogs.setFilters}
            onReset={state.resetFiltersAndSearch}
            dateRangeValue={dateRangeValue}
          />
        }
      >
        <EvimoTable<AssetLogListItem>
          className={styles.table}
          tableWrapRef={state.tableWrapRef}
          dataSource={state.filteredLogs}
          columns={columns}
          loading={assetLogs.isLoading}
          rowKey="logId"
          locale={{ emptyText: t('noLogs') }}
          pagination={false}
          scrollBody={false}
          size="middle"
          variant="technical"
          rowClassName={(record) =>
            state.expandedRowKeys.includes(record.logId)
              ? styles.expandedRow
              : ''
          }
          onRow={(record) => ({
            onClick: () => {
              state.toggleExpandedRow(record.logId);
            },
            style: { cursor: 'pointer' },
          })}
          expandable={{
            expandedRowKeys: state.expandedRowKeys,
            onExpandedRowsChange: (keys) => {
              state.setExpandedFromKeys(keys);
            },
            expandRowByClick: false,
            expandedRowRender: (log) => (
              <div className={styles.expandedMotion}>
                <AssetLogExpandedDetails
                  log={log}
                  onEdit={state.setSelectedLog}
                />
              </div>
            ),
            expandIcon: ({ expanded, onExpand, record }) => (
              <button
                type="button"
                className={styles.expandButton}
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? t('detail.logs.collapseRow', 'Collapse log details')
                    : t('detail.logs.expandRow', 'Expand log details')
                }
                onClick={(event) => {
                  event.stopPropagation();
                  onExpand(record, event);
                }}
              >
                <span
                  className={[
                    styles.chevron,
                    expanded ? styles.chevronExpanded : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <DownOutlined />
                </span>
              </button>
            ),
          }}
          footer={() =>
            state.showInfiniteFooter ? (
              <div className={styles.infiniteFooter}>
                <div
                  className={styles.infiniteFooterSlot}
                  aria-live="polite"
                  aria-busy={assetLogs.isFetchingNextPage}
                >
                  {assetLogs.isFetchingNextPage ? (
                    <Loader
                      size="small"
                      className={styles.infiniteLoader}
                      description={t(
                        'detail.logs.loadingMore',
                        'Loading more logs...',
                      )}
                    />
                  ) : assetLogs.isFetchNextPageError ? (
                    <div className={styles.infiniteErrorRow}>
                      <Typography.Text className={styles.infiniteStatus}>
                        {t(
                          'detail.logs.loadMoreError',
                          'Could not load more logs.',
                        )}
                      </Typography.Text>
                      <AntButton
                        size="small"
                        onClick={() => {
                          void assetLogs.fetchNextPage();
                        }}
                      >
                        {t('detail.logs.retryLoadMore', 'Retry')}
                      </AntButton>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null
          }
        />
      </Card>

      <AssetLogModal
        log={state.selectedLog}
        open={!!state.selectedLog}
        isSaving={isUpdatingLog}
        onClose={() => state.setSelectedLog(null)}
        onSave={handleUpdateLog}
      />
    </>
  );
}
